/**
 * Sidebar v2 drop plan application: dispatches a resolved drop plan's
 * lifecycle actions and order-key writes through the supplied callbacks.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (drop writers) at pin e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: order-key and lifecycle writes land on
 * forge's client-side overlay (uiStateStore) instead of upstream's
 * server-side thread commands.
 */
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import type { SidebarThreadDropPlan } from "./sidebarV2DropPlan";

/** The key writes one landed drop performs: the fresh-pin key for the
 * lifted row, then the neighbor keys. */
function planAssignments(
  plan: Exclude<SidebarThreadDropPlan, { kind: "none" } | { kind: "settle" }>,
  activeId: string,
): ReadonlyArray<{ readonly id: string; readonly orderKey: string }> {
  if (plan.kind === "pin") {
    return [
      ...(plan.orderKey === undefined ? [] : [{ id: activeId, orderKey: plan.orderKey }]),
      ...plan.extraAssignments,
    ];
  }
  if (plan.kind === "reorder-pinned" || plan.kind === "move-active") return plan.assignments;
  return [];
}

/** Dispatch lifecycle actions and order-key writes for a nonempty drop plan through the supplied callbacks. */
// fallow-ignore-next-line complexity -- decomposed from the byte-faithful upstream projection and reorder planner (pin e3e7cc3f, #3601 precedent)
export function applyThreadDragPlan(
  plan: Exclude<SidebarThreadDropPlan, { kind: "none" }>,
  context: {
    readonly activeId: string;
    readonly threadRef: ReturnType<typeof scopeThreadRef>;
    readonly attemptSettle: (threadRef: ScopedThreadRef) => void;
    readonly attemptUnsettle: (threadRef: ScopedThreadRef) => void;
    readonly pinThread: (threadRef: ScopedThreadRef) => void;
    readonly setThreadPinned: (threadId: string, pinned: boolean) => void;
    readonly setThreadSnooze: (threadId: string, snooze: null) => void;
    readonly setThreadPinOrderKey: (threadId: string, orderKey: string | null) => void;
    readonly setThreadActiveOrderKey: (threadId: string, orderKey: string | null) => void;
  },
): void {
  const threadRef = context.threadRef;
  if (plan.kind === "settle") {
    context.attemptSettle(threadRef);
    return;
  }
  const assignments = planAssignments(plan, context.activeId);
  if (plan.kind === "move-active") {
    if (plan.unpin) context.setThreadPinned(context.activeId, false);
    if (plan.unsettle) context.attemptUnsettle(threadRef);
    if (plan.unsnooze) context.setThreadSnooze(context.activeId, null);
  }
  if (plan.kind === "pin") context.pinThread(threadRef);
  // Every assignment lands, including the active thread's under a pin
  // plan: the fresh top key from pinThread is written first and the
  // planned key then replaces it, so the lifted row ends in the drop slot
  // instead of staying at the top of the run.
  for (const assignment of assignments) {
    if (plan.kind === "move-active") {
      context.setThreadActiveOrderKey(assignment.id, assignment.orderKey);
    } else {
      context.setThreadPinOrderKey(assignment.id, assignment.orderKey);
    }
  }
}
